import { useState } from 'react';
import { message } from 'antd';
import { captureException } from '@sentry/react';
import { useIntl } from 'react-intl';
import { getInventoryMetersApi } from '../api';
import { getInventoryMetersDbApi } from '../api/indexed_db/inventoryMetersDb';
import { InventoryMeterRecordDb } from '../indexed_db/inventoryMetersDB';
import { useScrollTopOnMount } from './useScrollTopOnMount';
import { useStore } from '../routes/inventorize_meter/useStore';
import { customApiKeyAuthenticator } from '../services/httpClient';
import { SynchronizeStatus, SynchronizeStep } from '../consts/sync';
import { SynchronizeMeters as messages } from './messages';

const sendItem = async ({ id, meter: inventoryMeter, locationApiKey }: InventoryMeterRecordDb) => {
  await getInventoryMetersApi().inventorize(inventoryMeter, {
    authenticator: customApiKeyAuthenticator(locationApiKey),
  });
  await getInventoryMetersDbApi().setSyncedToRecord(id);
};

export interface SynchronizeMetersShape {
  syncStatus: SynchronizeStatus;
  syncOfflineRecords: () => void;
}

export const useSynchronizeMeters = (): SynchronizeMetersShape => {
  const forceScrollTop = useScrollTopOnMount();
  const { queuedMetersCount, setQueuedMetersCount, setIsSendingData, reset } = useStore();
  const intl = useIntl();

  const [syncStatus, setSyncStatus] = useState<SynchronizeStatus>({
    syncStep: SynchronizeStep.Confirm,
    queuedCount: queuedMetersCount ?? 0,
    syncedCount: 0,
    rejectedCount: 0,
  });

  const synchronizeInventoryMeters = async (items: InventoryMeterRecordDb[]) => {
    // we want to send meters sequentially
    for (const item of items) {
      try {
        await sendItem(item);
        setSyncStatus(prevState => ({
          ...prevState,
          syncStep: SynchronizeStep.InProgress,
          syncedCount: prevState.syncedCount + 1,
        }));
      } catch (err) {
        message.error(intl.formatMessage(messages.errorMessage, { meterId: item.meter.data.serial }));
        console.error(err);
        captureException(err);
        setSyncStatus(prevState => ({
          ...prevState,
          syncStep: SynchronizeStep.InProgress,
          rejectedCount: prevState.rejectedCount + 1,
        }));
      }
    }
  };

  const syncOfflineRecords = async () => {
    setSyncStatus(prevState => ({ ...prevState, syncStep: SynchronizeStep.GetReady }));
    setIsSendingData(true);

    const inventoryMetersData = await getInventoryMetersDbApi().getData();
    await synchronizeInventoryMeters(inventoryMetersData.filter(item => !item.wasSynced));

    setSyncStatus(prevState => ({ ...prevState, syncStep: SynchronizeStep.Finished }));

    // update queued meters count
    const inventoryMetersDb = getInventoryMetersDbApi();
    const getQueuedMetersCount = async () => {
      const metersCount = await inventoryMetersDb.countNotSynchronized();
      setQueuedMetersCount(metersCount);
    };
    getQueuedMetersCount();

    setIsSendingData(false);
    reset();
    forceScrollTop();
  };

  return { syncStatus, syncOfflineRecords };
};
